import { useEffect, useRef } from 'react';
import { jwtDecode } from 'jwt-decode';

import { useTenantProfile } from '../hooks/partnerPortal';
import { changeLanguage } from '../utils/languageUtils';
import SecureCookieManager from '../utils/secureCookie';
import { getV2Token } from '../utils/v2AuthUtils';

const PARTNER_ARCHETYPES = new Set(['consulting_firm', 'audit_firm']);

function readToken(): string | null {
  return SecureCookieManager.getAuthToken() || getV2Token() || null;
}

function isPartner(): boolean {
  const token = readToken();
  if (!token) return false;
  try {
    const decoded: any = jwtDecode(token);
    return PARTNER_ARCHETYPES.has(decoded?.archetype);
  } catch {
    return false;
  }
}

/**
 * Reads the partner's tenant profile (default_language + ui_direction) on
 * mount and applies the language + RTL direction — but ONLY the first
 * time, when the user hasn't yet set a preference of their own. After
 * the user toggles language manually (via the header switch), their
 * choice is stored in localStorage and is sticky across refreshes;
 * we never override an explicit user preference.
 *
 * Non-partner users keep the existing manual-toggle flow — the
 * useTenantProfile query is gated by archetype so non-partners never call
 * the partner-only endpoint.
 */
const USER_PREF_KEYS = ['app_language', 'shared_app_language', 'selectedLanguage'];

function hasUserLanguagePreference(): boolean {
  try {
    return USER_PREF_KEYS.some((k) => localStorage.getItem(k));
  } catch {
    return false;
  }
}

export default function AutoTenantLanguage() {
  const partner = isPartner();
  const { data } = useTenantProfile({ enabled: partner });
  const lastApplied = useRef<string | null>(null);

  useEffect(() => {
    if (!partner || !data) return;
    // If the user already has a saved language preference (from prior
    // manual toggle OR from the tenant default we applied on first
    // login), never override on refresh. This was the source of the
    // "language flips back to Arabic on every refresh" bug.
    if (hasUserLanguagePreference()) return;
    const lang =
      data.default_language === 'ar' || data.ui_direction === 'rtl' ? 'ar' : 'en';
    if (lastApplied.current === lang) return;
    lastApplied.current = lang;
    changeLanguage(lang);
  }, [partner, data]);

  return null;
}
